<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-file-text me-2"></i>
              Logs del Script #{{ scriptId }}
            </h1>
            <p class="text-muted mb-0">
              Visualización de logs en tiempo real
            </p>
          </div>
          <router-link
            to="/scripts"
            class="btn btn-outline-secondary"
          >
            <i class="bi bi-arrow-left me-1"></i>
            Volver a Scripts
          </router-link>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="!script" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="text-muted mt-2">Cargando información del script...</p>
    </div>

    <!-- Main content -->
    <div v-else>
      <!-- Script Info -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <h6 class="mb-2">Información del Script</h6>
              <div class="small">
                <div class="mb-1">
                  <strong>Cliente:</strong> {{ script.client_name }}
                </div>
                <div class="mb-1">
                  <strong>Base de datos:</strong> {{ script.geotab_database }}
                </div>
                <div class="mb-1">
                  <strong>Estado:</strong>
                  <span :class="getStatusBadgeClass(script.status)">
                    {{ getStatusText(script.status) }}
                  </span>
                </div>
                <div v-if="script.pid" class="mb-1">
                  <strong>PID:</strong> <span class="badge bg-dark">{{ script.pid }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <h6 class="mb-2">Proceso</h6>
              <div v-if="processStatus" class="small">
                <div class="mb-1">
                  <strong>Estado:</strong>
                  <span :class="processStatus.is_running ? 'text-success' : 'text-danger'">
                    {{ processStatus.is_running ? 'Ejecutando' : 'Detenido' }}
                  </span>
                </div>
                <div v-if="processStatus.is_running" class="mb-1">
                  <strong>CPU:</strong> {{ processStatus.cpu_percent.toFixed(2) }}%
                </div>
                <div v-if="processStatus.is_running" class="mb-1">
                  <strong>Memoria:</strong> {{ processStatus.memory_mb.toFixed(2) }} MB
                </div>
              </div>
              <div v-else class="small text-muted">
                <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                Cargando estado del proceso...
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Log Type Tabs -->
      <ul class="nav nav-tabs mb-3" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'stdout' }"
            type="button"
            @click="activeTab = 'stdout'"
          >
            <i class="bi bi-terminal me-1"></i>
            STDOUT
            <span v-if="stdoutLogs" class="badge bg-primary ms-1">
              {{ stdoutLogs.total_lines || 0 }}
            </span>
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'stderr' }"
            type="button"
            @click="activeTab = 'stderr'"
          >
            <i class="bi bi-exclamation-triangle me-1"></i>
            STDERR
            <span v-if="stderrLogs" class="badge bg-danger ms-1">
              {{ stderrLogs.total_lines || 0 }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Log Content -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span>
            <i class="bi bi-card-list me-1"></i>
            {{ activeTab === 'stdout' ? 'Salida Estándar' : 'Salida de Errores' }}
          </span>
          <div class="btn-group btn-group-sm">
            <button
              class="btn btn-outline-secondary"
              @click="refreshLogs(true)"
              :disabled="loadingLogs"
            >
              <i class="bi bi-arrow-clockwise"></i>
              Actualizar
            </button>
            <button
              class="btn btn-outline-secondary"
              @click="downloadLogs"
            >
              <i class="bi bi-download"></i>
              Descargar
            </button>
            <button
              class="btn btn-outline-primary"
              @click="parseLogs"
              :disabled="parsingLogs"
              title="Procesar log para extraer información de dispositivos"
            >
              <span v-if="parsingLogs" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-cpu me-1"></i>
              Procesar Log
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <!-- Loading -->
          <div v-if="loadingLogs" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Cargando logs...</span>
            </div>
            <p class="text-muted mt-2">Cargando logs...</p>
          </div>

          <!-- Log Content -->
          <div v-else-if="currentLogs" class="log-content" ref="logContainer">
            <!-- New content notification -->
            <div
              v-if="hasNewContent && !isScrolledToBottom"
              class="new-content-badge"
              @click="scrollToBottom"
            >
              <i class="bi bi-arrow-down-circle me-1"></i>
              Nuevo contenido disponible - Click para ver
            </div>

            <div v-if="!currentLogs.content || currentLogs.content.trim() === ''">
              <div class="text-center py-5 text-muted">
                <i class="bi bi-inbox fs-1"></i>
                <p class="mt-2">No hay logs disponibles</p>
              </div>
            </div>
            <pre v-else class="mb-0 p-3"><code>{{ currentLogs.content }}</code></pre>
          </div>

          <!-- Error -->
          <div v-else class="text-center py-5">
            <i class="bi bi-exclamation-triangle fs-1 text-warning"></i>
            <p class="text-muted mt-2">Error al cargar los logs</p>
            <button class="btn btn-sm btn-primary" @click="refreshLogs">
              Reintentar
            </button>
          </div>
        </div>
        <div class="card-footer text-muted small">
          <div class="row">
            <div class="col-md-6">
              <strong>Archivo:</strong> {{ currentLogs?.log_file || 'N/A' }}
            </div>
            <div class="col-md-6 text-end">
              <strong>Líneas mostradas:</strong> {{ linesToShow }} últimas
            </div>
          </div>
        </div>
      </div>

      <!-- Lines to show selector -->
      <div class="mt-3">
        <label for="linesToShow" class="form-label small">
          Número de líneas a mostrar:
        </label>
        <select
          id="linesToShow"
          v-model.number="linesToShow"
          class="form-select form-select-sm"
          style="width: auto; display: inline-block"
          @change="refreshLogs"
        >
          <option :value="50">50 líneas</option>
          <option :value="100">100 líneas</option>
          <option :value="200">200 líneas</option>
          <option :value="500">500 líneas</option>
          <option :value="1000">1000 líneas</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useScriptStore } from '../../stores/scripts'

const route = useRoute()
const scriptStore = useScriptStore()

// State
const scriptId = ref(parseInt(route.params.id))
const script = ref(null)
const activeTab = ref('stdout')
const loadingLogs = ref(false)
const stdoutLogs = ref(null)
const stderrLogs = ref(null)
const processStatus = ref(null)
const linesToShow = ref(100)
const autoRefreshInterval = ref(null)
const logContainer = ref(null)
const isScrolledToBottom = ref(true)
const hasNewContent = ref(false)
const previousContentLength = ref(0)
const parsingLogs = ref(false)

// Computed
const currentLogs = computed(() => {
  return activeTab.value === 'stdout' ? stdoutLogs.value : stderrLogs.value
})

// Methods
const loadScriptInfo = async () => {
  try {
    // Fetch script info from store or API
    await scriptStore.fetchScripts()
    script.value = scriptStore.scripts.find(s => s.id === scriptId.value)
  } catch (error) {
    console.error('Error loading script info:', error)
  }
}

const refreshLogs = async (forceScroll = false) => {
  // Don't show loading spinner for auto-refresh to avoid disruption
  const isAutoRefresh = !forceScroll && autoRefreshInterval.value !== null
  if (!isAutoRefresh) {
    loadingLogs.value = true
  }

  try {
    // Store current scroll position
    const container = logContainer.value
    const wasAtBottom = isScrolledToBottom.value
    let scrollPositionFromTop = 0

    if (container) {
      scrollPositionFromTop = container.scrollTop
    }

    // Fetch both logs in parallel
    const [stdout, stderr, status] = await Promise.all([
      scriptStore.getLogs(scriptId.value, 'stdout', linesToShow.value),
      scriptStore.getLogs(scriptId.value, 'stderr', linesToShow.value),
      scriptStore.getProcessStatus(scriptId.value)
    ])

    // Check if content has changed
    const currentContent = activeTab.value === 'stdout' ? stdout : stderr
    const currentContentLength = currentContent?.content?.length || 0
    const hasChanged = currentContentLength !== previousContentLength.value

    if (hasChanged && !wasAtBottom && isAutoRefresh) {
      // Content changed but user is not at bottom - show notification
      hasNewContent.value = true
    }

    previousContentLength.value = currentContentLength

    stdoutLogs.value = stdout
    stderrLogs.value = stderr
    processStatus.value = status

    // Restore scroll position or scroll to bottom
    await nextTick()

    if (container) {
      if (wasAtBottom || forceScroll) {
        // User was at bottom or manual refresh - scroll to bottom
        scrollToBottom()
        hasNewContent.value = false
      } else if (!isAutoRefresh) {
        // Manual refresh while not at bottom - restore position
        container.scrollTop = scrollPositionFromTop
      }
      // If auto-refresh and not at bottom, maintain current position
    }
  } catch (error) {
    console.error('Error loading logs:', error)
  } finally {
    loadingLogs.value = false
  }
}

const scrollToBottom = () => {
  const container = logContainer.value
  if (container) {
    container.scrollTop = container.scrollHeight
    isScrolledToBottom.value = true
    hasNewContent.value = false
  }
}

const handleScroll = () => {
  const container = logContainer.value
  if (!container) return

  // Check if user is at bottom (with 50px threshold)
  const threshold = 50
  const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold
  isScrolledToBottom.value = isAtBottom

  // If user scrolled to bottom, clear new content notification
  if (isAtBottom) {
    hasNewContent.value = false
  }
}

const downloadLogs = () => {
  if (!currentLogs.value || !currentLogs.value.content) {
    alert('No hay contenido para descargar')
    return
  }

  const logType = activeTab.value
  const content = currentLogs.value.content
  const filename = `script_${scriptId.value}_${logType}_${new Date().toISOString().slice(0, 10)}.log`

  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const parseLogs = async () => {
  if (!confirm('¿Deseas procesar este log para extraer información de dispositivos?\n\nEsto creará/actualizará registros en la base de datos.')) {
    return
  }

  // Debug: verify parseLogs method exists
  console.log('scriptStore methods:', Object.keys(scriptStore))
  console.log('parseLogs exists?', typeof scriptStore.parseLogs)

  parsingLogs.value = true
  try {
    const logType = activeTab.value

    // Check if parseLogs method exists before calling
    if (typeof scriptStore.parseLogs !== 'function') {
      throw new Error('El método parseLogs no está disponible en el store. Por favor recarga la página.')
    }

    const result = await scriptStore.parseLogs(scriptId.value, logType)

    // Show success message with statistics
    let message = `Log procesado exitosamente!\n\n`
    message += `📊 Estadísticas:\n`
    message += `- Líneas procesadas: ${result.lines_processed}\n`
    message += `- Líneas DEVICE_STATUS: ${result.device_status_lines}\n`
    message += `- Dispositivos encontrados: ${result.devices_found}\n`
    message += `- Reportes creados: ${result.reports_created}\n`
    message += `- Reportes actualizados: ${result.reports_updated}\n`

    if (result.errors && result.errors.length > 0) {
      message += `\n⚠️ Errores (${result.errors.length}):\n`
      message += result.errors.slice(0, 5).join('\n')
      if (result.errors.length > 5) {
        message += `\n... y ${result.errors.length - 5} más`
      }
    }

    alert(message)
  } catch (error) {
    console.error('Error parsing logs:', error)
    alert(`Error al procesar el log: ${error.response?.data?.detail || error.message}`)
  } finally {
    parsingLogs.value = false
  }
}

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'badge bg-secondary',
    running: 'badge bg-success',
    stopped: 'badge bg-warning',
    failed: 'badge bg-danger',
    completed: 'badge bg-info'
  }
  return classes[status] || 'badge bg-secondary'
}

const getStatusText = (status) => {
  const texts = {
    pending: 'Pendiente',
    running: 'En Ejecución',
    stopped: 'Detenido',
    failed: 'Fallido',
    completed: 'Completado'
  }
  return texts[status] || status
}

const startAutoRefresh = () => {
  // Auto-refresh logs every 5 seconds
  autoRefreshInterval.value = setInterval(() => {
    refreshLogs(false) // false = don't force scroll
  }, 5000)
}

const stopAutoRefresh = () => {
  if (autoRefreshInterval.value) {
    clearInterval(autoRefreshInterval.value)
    autoRefreshInterval.value = null
  }
}

// Watch for tab changes
watch(activeTab, () => {
  hasNewContent.value = false
  previousContentLength.value = currentLogs.value?.content?.length || 0
  // Scroll to bottom when switching tabs
  nextTick(() => {
    scrollToBottom()
  })
})

// Lifecycle
onMounted(async () => {
  await loadScriptInfo()
  await refreshLogs(true) // Initial load - scroll to bottom
  startAutoRefresh()

  // Add scroll listener to log container
  nextTick(() => {
    const container = logContainer.value
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
  })
})

onUnmounted(() => {
  stopAutoRefresh()

  // Remove scroll listener
  const container = logContainer.value
  if (container) {
    container.removeEventListener('scroll', handleScroll)
  }
})
</script>

<style scoped>
.log-content {
  position: relative;
  max-height: 500px;
  overflow-y: auto;
  background-color: #1e1e1e;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  scroll-behavior: smooth;
}

.log-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.log-content code {
  color: #d4d4d4;
}

.new-content-badge {
  position: sticky;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
  margin: 10px auto;
  text-align: center;
  animation: pulse 2s infinite;
}

.new-content-badge:hover {
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

.nav-tabs .nav-link {
  color: var(--bs-secondary);
}

.nav-tabs .nav-link.active {
  color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.nav-tabs .nav-link:hover {
  border-color: transparent;
}
</style>
